import React, { useState, useEffect, useRef } from "react";
import { generateQrCodeUrl, encodeQrCodeText } from "../../utils/qrCodeUtils";
import QRCode from "qrcode";
import { Move, RotateCcw, Type, Palette } from "lucide-react";
import { getFontById, DEFAULT_FONT } from "../../config/fonts";
import { normalizeShape } from "../../utils/tagShapeOrientation";
import { computeEngraveFontSizesPx } from "../../utils/engravePreview";
import {
  TAG_DESIGN_PRESETS,
  applyShapeAdjustments,
} from "../../utils/tagDesignPresets";
import { formatEngraveDisplay, getLineFormat } from "../../utils/tagLineFormat";
// Import accessory SVGs
import cleanPaw from "../../assets/Materials/Accessories/clean_paw.svg";
import superCleanPaw from "../../assets/Materials/Accessories/super_clean_paw.svg";
import dirtyPaw from "../../assets/Materials/Accessories/dirty_paw.svg";
import bonePaw from "../../assets/Materials/Accessories/bone_paw.svg";
import pawAndClawPixel from "../../assets/Materials/Accessories/paw_and_claw_pixel.svg";
import pixelPaws from "../../assets/Materials/Accessories/pixel_paws.svg";
import pixelHeart from "../../assets/Materials/Accessories/pixel_heart.svg";
import simpleHeart from "../../assets/Materials/Accessories/simple_heart.svg";
import heartNecklace from "../../assets/Materials/Accessories/heart_shape_necklace.svg";
import dogTag from "../../assets/Materials/Accessories/dog_tag.svg";

// Accessory registry — IDs must match AccessoryPicker.js
export const ACCESSORY_ASSETS = {
  clean_paw:          cleanPaw,
  super_clean_paw:    superCleanPaw,
  dirty_paw:          dirtyPaw,
  bone_paw:           bonePaw,
  paw_and_claw_pixel: pawAndClawPixel,
  pixel_paws:         pixelPaws,
  pixel_heart:        pixelHeart,
  simple_heart:       simpleHeart,
  heart_necklace:     heartNecklace,
  dog_tag:            dogTag,
};

// Preview is a plain "engrave surface" text card — no shape outline is drawn.
// Physical shape/color is still chosen earlier in the flow (Shape/Color steps);
// this box just needs to show legible engraved text with good contrast, so one
// consistent reference size works for every material.
const TAG_DISPLAY = { width: 360, height: 260 };
const TAG_ASPECT_RATIO = TAG_DISPLAY.width / TAG_DISPLAY.height;
// Insets used purely to keep text off the edges of the available space —
// not a shape boundary, just breathing room.
const GENERIC_INSETS = { top: 20, right: 16, bottom: 14, left: 16 };
const GENERIC_INSET_STYLE = {
  paddingTop: `${GENERIC_INSETS.top}%`,
  paddingRight: `${GENERIC_INSETS.right}%`,
  paddingBottom: `${GENERIC_INSETS.bottom}%`,
  paddingLeft: `${GENERIC_INSETS.left}%`,
};

/**
 * Compute where an accessory should sit to avoid the text block.
 *
 * Returns { x, y, size } where x/y are % positions within the tag container
 * and size is the SVG width/height in px.
 *
 * "zone" is a named region:
 *   'top' | 'bottom' | 'left' | 'right'
 *   'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
 *   'auto' → picks the best free zone given how many text lines are filled
 */
const ZONES = {
  top:            { x: 50, y: 13, size: 26 },
  bottom:         { x: 50, y: 87, size: 26 },
  left:           { x: 8,  y: 50, size: 22 },
  right:          { x: 92, y: 50, size: 22 },
  "top-left":     { x: 20, y: 22, size: 20 },
  "top-right":    { x: 80, y: 22, size: 20 },
  "bottom-left":  { x: 20, y: 78, size: 20 },
  "bottom-right": { x: 80, y: 78, size: 20 },
};

// Priority order of zones to check for each text-density level
const ZONE_PRIORITY = {
  0: ['top', 'bottom', 'left', 'right', 'top-left', 'top-right'],
  1: ['top', 'bottom', 'left', 'right'],
  2: ['top', 'bottom', 'left', 'right'],
  3: ['left', 'right', 'top-left', 'top-right', 'bottom-left', 'bottom-right', 'top'],
};

const computeAccessoryPlacement = (sideConfig, requestedZone) => {
  const linesFilled = [sideConfig?.line1, sideConfig?.line2, sideConfig?.line3]
    .filter(Boolean).length;

  // If a specific zone was requested, use it
  if (requestedZone && requestedZone !== 'auto' && ZONES[requestedZone]) {
    return ZONES[requestedZone];
  }

  // Auto: walk through priority zones for how full the text block is
  const priorities = ZONE_PRIORITY[Math.min(linesFilled, 3)] || ZONE_PRIORITY[0];
  for (const zone of priorities) {
    if (ZONES[zone]) return ZONES[zone];
  }

  // Ultimate fallback
  return Object.values(ZONES)[0] || { x: 50, y: 15, size: 24 };
};

/**
 * Unified TagPreview Component
 * Merges TagPreview + EnhancedTagPreview functionality
 *
 * Features:
 * - Font selection via selectedFont prop (from FontDropdown)
 * - Design presets via selectedDesign prop (optional)
 * - Text case toggle (title/uppercase)
 * - Dark mode support
 * - Accessory/decoration rendering
 * - QR code positioning and dragging
 * - Two-sided flip animation
 * - Text auto-fits the card and auto-picks a contrasting color — no shape
 *   outline and no manual size control, both are inferred from the material
 *   and the text itself.
 */
const TagPreview = ({
  material,
  orderType,
  formData,
  qrCodePosition = { x: 50, y: 50 },
  onQrPositionChange,
  side1Config = {},
  side2Config = {},
  qrCodeSide = 2,
  qrCodeText = { line1: "", line2: "", line3: "" },
  selectedFont = null,
  selectedDesign = null,
  textCase = "title",
  onTextCaseChange = null,
  accessories = [], // Array of { side, type, position_x, position_y, size }
  currentSide: controlledSide = null, // optional controlled side from parent
  embedded = false, // true = skip own card chrome, fill parent's container instead
  fillContainer = false, // true = card scales to fill available space (preserves aspect ratio)
  previewScale = 1, // multiplier on the base card size when fillContainer is true
  className = "",
}) => {
  const [qrCodeValue, setQrCodeValue] = useState("");
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState(null);

  // Kept only for the (currently unused-in-production) design-preset font
  // adjustments below — no longer drives any shape rendering.
  const normalizedShape = material ? normalizeShape(material.shape) : "circle";
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [currentSide, setCurrentSide] = useState(controlledSide ?? 1);

  // Measure the card's actual rendered width so text can be sized in real
  // px instead of CSS container-query units. cqw-based sizing kept breaking
  // (resolving against the wrong ancestor's width, and separately behaving
  // unpredictably combined with the app-wide `zoom: 125%` rule) — a
  // ResizeObserver measurement is slower to react by a frame but is not
  // subject to either problem, so it's the more reliable choice here.
  const cardRef = useRef(null);
  const [cardWidthPx, setCardWidthPx] = useState(TAG_DISPLAY.width);

  useEffect(() => {
    const node = cardRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width;
      if (width) setCardWidthPx(width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Sync to controlled side from parent (e.g. when form flips sides)
  useEffect(() => {
    if (controlledSide !== null && controlledSide !== currentSide) {
      setTimeout(() => {
        setCurrentSide(controlledSide);
      }, 250);
    }
  }, [controlledSide]);

  // Self-flip is only for callers that don't pass currentSide (e.g. the
  // review step, which has no side-toggle control of its own). When a
  // parent DOES pass currentSide (design step, accessory step — both have
  // their own "Side 1 / Side 2" control in the surrounding form), this is
  // unused: the old internal "Flip Tag" button was removed from those cases
  // because it changed currentSide locally without telling the parent, so
  // the preview could show a different side than the one actually being
  // edited in the form.
  const toggleSide = () => {
    setCurrentSide((prev) => (prev === 1 ? 2 : 1));
  };

  // Generate QR code value from text or URL
  useEffect(() => {
    if (orderType === "database") {
      if (qrCodeText?.line1 || qrCodeText?.line2 || qrCodeText?.line3) {
        const encodedText = encodeQrCodeText(
          qrCodeText.line1 || "",
          qrCodeText.line2 || "",
          qrCodeText.line3 || ""
        );
        setQrCodeValue(encodedText);
      } else if (formData?.userid) {
        const url = generateQrCodeUrl(formData.userid);
        setQrCodeValue(url);
      }
    }
  }, [orderType, formData?.userid, qrCodeText]);

  // Generate QR code image when value changes
  useEffect(() => {
    if (qrCodeValue) {
      QRCode.toDataURL(qrCodeValue, {
        width: 60,
        margin: 1,
        errorCorrectionLevel: "H",
        color: {
          dark: "#000000",
          light: "#FFFFFF",
        },
      })
        .then((dataUrl) => {
          setQrCodeDataUrl(dataUrl);
        })
        .catch((error) => {
          console.error("Error generating QR code:", error);
          setQrCodeDataUrl(null);
        });
    } else {
      setQrCodeDataUrl(null);
    }
  }, [qrCodeValue]);

  const handleMouseDown = (e) => {
    if (orderType !== "database" || currentSide !== qrCodeSide || !qrCodeValue)
      return;
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setDragStart({
      x: e.clientX - rect.left - qrCodePosition.x,
      y: e.clientY - rect.top - qrCodePosition.y,
    });
  };

  const handleMouseMove = (e) => {
    if (
      !isDragging ||
      orderType !== "database" ||
      currentSide !== qrCodeSide ||
      !qrCodeValue
    )
      return;
    const rect = e.currentTarget.getBoundingClientRect();
    const newX = Math.max(
      10,
      Math.min(90, ((e.clientX - rect.left - dragStart.x) / rect.width) * 100)
    );
    const newY = Math.max(
      10,
      Math.min(90, ((e.clientY - rect.top - dragStart.y) / rect.height) * 100)
    );

    if (onQrPositionChange) {
      onQrPositionChange({ x: newX, y: newY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Get font style - prioritize selectedFont prop, fallback to design preset
  const getFontStyle = (fontId) => {
    // First try the explicitly selected font
    if (fontId || selectedFont) {
      const font = getFontById(fontId || selectedFont);
      if (font) {
        return {
          fontFamily: font.family,
          fontWeight:
            font.style === "Bold" || font.style === "Statement"
              ? "700"
              : font.style === "Modern"
              ? "600"
              : "400",
          fontStyle: font.style === "Elegant" ? "italic" : "normal",
          letterSpacing: font.style === "Retro" ? "0.1em" : "0.02em",
        };
      }
    }

    // Fallback to design preset if available
    if (selectedDesign) {
      const appliedDesign = applyShapeAdjustments(
        selectedDesign,
        normalizedShape
      );
      return {
        fontFamily: appliedDesign.fontFamily,
        fontWeight: appliedDesign.fontWeight || "600",
        fontStyle: appliedDesign.fontStyle || "normal",
        letterSpacing: appliedDesign.letterSpacing || "0.02em",
      };
    }

    // Ultimate fallback to default font
    const defaultFont = DEFAULT_FONT;
    return {
      fontFamily: defaultFont.family,
      fontWeight: "600",
      fontStyle: "normal",
      letterSpacing: "0.02em",
    };
  };

  // No shape at all: no background fill, no border, no corner radius, no
  // keyring-tab graphic, no dashed guide. Just the text, in a color that
  // reads clearly against the app's own light/dark background (the same
  // text-color tokens used everywhere else in the UI).
  const textColorClassName = "text-light-text dark:text-dark-text";

  // Format text based on textCase setting
  const formatText = (text, caseType) => {
    if (!text) return "";
    if (caseType === "uppercase") {
      return text.toUpperCase();
    }
    // Title case: capitalize first letter of each word
    return text
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Render accessories for a given side, auto-placed to avoid text
  const renderAccessories = (side) => {
    const sideAccessories = accessories.filter((acc) => acc.side === side);
    const sideConfig = side === 1 ? side1Config : side2Config;

    return sideAccessories.map((accessory, idx) => {
      const svgSrc = ACCESSORY_ASSETS[accessory.type];
      if (!svgSrc) return null;

      // Compute position — explicit position_x/y overrides auto placement
      const placement = computeAccessoryPlacement(sideConfig, accessory.zone || 'auto');
      const px = accessory.position_x ?? placement.x;
      const py = accessory.position_y ?? placement.y;
      const sz = accessory.size ?? placement.size;

      return (
        <img
          key={`accessory-${side}-${idx}`}
          src={svgSrc}
          alt={accessory.type}
          // The source SVGs are solid black — invert them to white in dark
          // mode (same rationale as the engraved text color) so they don't
          // disappear against the dark background. Stay black in light mode,
          // where they already read fine against the light background.
          className="absolute pointer-events-none select-none drop-shadow-[0_1px_3px_rgba(0,0,0,0.35)] dark:brightness-0 dark:invert"
          style={{
            left: `${px}%`,
            top: `${py}%`,
            transform: "translate(-50%, -50%)",
            width: `${sz}px`,
            height: `${sz}px`,
            opacity: 0.85,
          }}
        />
      );
    });
  };

  // Render decorations from design preset
  const renderDecorations = (position, side) => {
    if (!selectedDesign?.decorations) return null;

    return selectedDesign.decorations
      .filter((decoration) => decoration.position === position)
      .map((decoration, idx) => {
        switch (decoration.type) {
          case "line":
            return (
              <div
                key={`deco-${side}-${idx}`}
                style={{
                  position: "absolute",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: position.includes("name") ? "60%" : "80%",
                  height: decoration.width,
                  backgroundColor: decoration.color,
                  ...(position === "under-name" && { top: "60%" }),
                  ...(position === "above-name" && { top: "25%" }),
                }}
              />
            );
          case "border":
            if (position === "around-text") {
              return (
                <div
                  key={`deco-${side}-${idx}`}
                  style={{
                    position: "absolute",
                    inset: "10px",
                    border: `${decoration.width} ${decoration.style} ${decoration.color}`,
                    borderRadius: "4px",
                    pointerEvents: "none",
                  }}
                />
              );
            }
            return null;
          case "icon":
            const iconContent =
              decoration.style === "paw"
                ? "🐾"
                : decoration.style === "tree"
                ? "🌲"
                : decoration.style === "star"
                ? "⭐"
                : "●";
            return (
              <div
                key={`deco-${side}-${idx}`}
                style={{
                  position: "absolute",
                  color: decoration.color,
                  fontSize: decoration.size || "14px",
                  ...(position === "left-of-name" && {
                    left: "10%",
                    top: "45%",
                  }),
                  ...(position === "right-of-name" && {
                    right: "10%",
                    top: "45%",
                  }),
                  ...(position === "above-name" && {
                    left: "50%",
                    top: "25%",
                    transform: "translateX(-50%)",
                  }),
                }}
              >
                {iconContent}
              </div>
            );
          case "flourish":
            return (
              <div
                key={`deco-${side}-${idx}`}
                style={{
                  position: "absolute",
                  left: "50%",
                  transform: "translateX(-50%)",
                  color: decoration.color,
                  fontSize: "12px",
                  ...(position === "above-name" && { top: "20%" }),
                  ...(position === "below-name" && { bottom: "20%" }),
                }}
              >
                ◈
              </div>
            );
          default:
            return null;
        }
      });
  };

  // Convert a px size computed against the TAG_DISPLAY reference box into
  // the equivalent px on the card's actual measured (ResizeObserver) width,
  // so text scales with however big the card really rendered.
  const cardScale = cardWidthPx / TAG_DISPLAY.width;
  const toResponsiveFontSize = (px) => `${(px * cardScale).toFixed(2)}px`;

  const renderSideContent = (sideNum) => {
    const config = sideNum === 1 ? side1Config : side2Config;
    const isQrCodeSide = orderType === "database" && qrCodeSide === sideNum;

    // If this side has QR code, don't render text content
    if (isQrCodeSide) {
      return null;
    }

    const fontId = config?.fontId || selectedFont || DEFAULT_FONT.id;
    const line1 = config?.line1 || "";
    const line2 = config?.line2 || "";
    const line3 = config?.line3 || "";

    if (!line1 && !line2 && !line3) return null;

    // Text auto-fits the card based on line count and length — no manual
    // size control needed.
    const fontPx = computeEngraveFontSizesPx(
      "circle",
      TAG_DISPLAY.width,
      TAG_DISPLAY.height,
      [
        { text: line1, tier: 0 },
        { text: line2, tier: 1 },
        { text: line3, tier: 2 },
      ]
    );

    // Each side has exactly 3 line-slots — a line must never wrap onto a
    // second visual row (that would silently eat one of the 3 slots).
    // computeEngraveFontSizesPx already shrinks the font to fit the line's
    // estimated width; nowrap + hidden overflow is the hard guarantee.
    const lineStyleBase = {
      lineHeight: "1.15",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "clip",
    };

    return (
      <div
        className="absolute inset-0 flex flex-col justify-center items-center text-center z-10 overflow-hidden"
        style={GENERIC_INSET_STYLE}
      >
        {/* Design preset decorations */}
        {renderDecorations("background", sideNum)}
        {renderDecorations("around-text", sideNum)}
        {renderDecorations("above-name", sideNum)}

        {line1 && (
          <div
            className={`font-bold mb-0.5 w-full max-w-full ${textColorClassName}`}
            style={{
              ...getFontStyle(fontId),
              ...lineStyleBase,
              fontSize: toResponsiveFontSize(fontPx.line1),
            }}
          >
            {formatEngraveDisplay(line1, getLineFormat(config, "line1"), textCase)}
          </div>
        )}

        {renderDecorations("under-name", sideNum)}

        {line2 && (
          <div
            className={`font-semibold mb-0.5 w-full max-w-full ${textColorClassName}`}
            style={{
              ...getFontStyle(fontId),
              ...lineStyleBase,
              fontSize: toResponsiveFontSize(fontPx.line2),
            }}
          >
            {formatEngraveDisplay(line2, getLineFormat(config, "line2"), textCase)}
          </div>
        )}
        {line3 && (
          <div
            className={`w-full max-w-full ${textColorClassName}`}
            style={{
              ...getFontStyle(fontId),
              ...lineStyleBase,
              fontSize: toResponsiveFontSize(fontPx.line3),
            }}
          >
            {formatEngraveDisplay(line3, getLineFormat(config, "line3"), textCase)}
          </div>
        )}

        {renderDecorations("below-name", sideNum)}

        {/* Render accessories for this side */}
        {renderAccessories(sideNum)}
      </div>
    );
  };

  const renderQrCode = (sideNum) => {
    if (
      orderType !== "database" ||
      !qrCodeValue ||
      !qrCodeDataUrl ||
      qrCodeSide !== sideNum
    ) {
      return null;
    }

    return (
      <div
        className="absolute cursor-move group z-20"
        style={{
          left: `${qrCodePosition.x}%`,
          top: `${qrCodePosition.y}%`,
          transform: "translate(-50%, -50%)",
        }}
        onMouseDown={handleMouseDown}
      >
        <div className="bg-white dark:bg-gray-800 p-1 rounded shadow-lg dark:shadow-xl border-2 border-gray-300 dark:border-gray-600 group-hover:border-red-500 dark:group-hover:border-coral-500 transition-colors">
          <img
            src={qrCodeDataUrl}
            alt="QR Code"
            width={60}
            height={60}
            className="block"
          />
          <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="bg-gray-800 dark:bg-gray-900 text-white dark:text-gray-100 text-xs px-2 py-1 rounded flex items-center gap-1 whitespace-nowrap border border-gray-700 dark:border-gray-600">
              <Move className="w-3 h-3" />
              Drag to position
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={
        embedded
          ? `h-full flex flex-col ${className}`
          : `bg-white dark:bg-dark-surface rounded-xl shadow-lg dark:shadow-card-dark p-4 border border-light-border dark:border-dark-border ${className}`
      }
    >
      <div className="flex items-center justify-between mb-3 shrink-0">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-bold text-light-text dark:text-dark-text">
            Tag Preview
          </h3>
          {material?.shapeName && (
            <span className="text-xs text-light-textMuted dark:text-dark-textMuted">
              {material.shapeName}
              {material.name ? ` · ${material.name}` : ""}
            </span>
          )}
          {selectedDesign && (
            <span className="inline-flex items-center gap-1 text-xs bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 px-2 py-1 rounded-full">
              <Palette className="w-3 h-3" />
              {selectedDesign.name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onTextCaseChange && (
            <button
              onClick={() => {
                const newCase = textCase === "title" ? "uppercase" : "title";
                onTextCaseChange(newCase);
              }}
              className="flex items-center gap-1 text-xs text-light-textMuted dark:text-dark-textMuted hover:text-light-primary dark:hover:text-dark-primary transition-colors px-2 py-1 rounded hover:bg-light-surface dark:hover:bg-dark-surfaceHover"
              title={
                textCase === "title"
                  ? "Switch to ALL CAPS"
                  : "Switch to Title Case"
              }
            >
              <Type className="w-3 h-3" />
              <span>{textCase === "title" ? "Title" : "ALL CAPS"}</span>
            </button>
          )}
          {controlledSide === null && (
            <button
              onClick={toggleSide}
              className="flex items-center gap-1 text-xs text-light-primary dark:text-dark-primary hover:text-light-primaryHover dark:hover:text-dark-primaryHover transition-colors"
            >
              <RotateCcw className="w-3 h-3" />
              <span>Flip Tag</span>
            </button>
          )}
        </div>
      </div>
      <div
        className={
          fillContainer
            ? "flex-1 min-h-0 flex justify-center items-center"
            : "flex justify-center"
        }
        // container-type:size implies CSS size containment, which needs a
        // definite height to work from. fillContainer mode gets one via
        // flex-1 inside a bounded-height ancestor, and its box-sizing
        // formula (min(100%, calc(100cqh * ratio))) needs the container
        // query. The fixed-size branch (e.g. Review step) has no explicit
        // height here and doesn't use any cq units for its own sizing — so
        // containment collapsed this div to 0 height, and the (still
        // correctly sized) card box just rendered outside/below it,
        // visually escaping the whole Tag Preview panel. Only apply it
        // where it's actually needed and safe.
        style={fillContainer ? { containerType: "size" } : undefined}
      >
        <div
          ref={cardRef}
          className="relative"
          style={
            fillContainer
              ? {
                  // Scale to fill whichever axis is the limiting one, without
                  // distorting the card (container-query units let this
                  // respond to the actual box size, not the viewport).
                  width: `min(100%, calc(100cqh * ${TAG_ASPECT_RATIO}))`,
                  height: `min(100%, calc(100cqw / ${TAG_ASPECT_RATIO}))`,
                  maxWidth: `${TAG_DISPLAY.width * previewScale}px`,
                  maxHeight: `${TAG_DISPLAY.height * previewScale}px`,
                  perspective: "1000px",
                }
              : {
                  width: `${TAG_DISPLAY.width}px`,
                  height: `${TAG_DISPLAY.height}px`,
                  perspective: "1000px",
                }
          }
        >
          <div
            className="relative w-full h-full transition-transform duration-500"
            style={{
              transformStyle: "preserve-3d",
              transform:
                currentSide === 2 ? "rotateY(180deg)" : "rotateY(0deg)",
            }}
          >
            {/* Side 1 - Front */}
            <div
              className="absolute inset-0 overflow-hidden"
              style={{
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
              }}
              onMouseMove={qrCodeSide === 1 ? handleMouseMove : undefined}
              onMouseUp={qrCodeSide === 1 ? handleMouseUp : undefined}
              onMouseLeave={qrCodeSide === 1 ? handleMouseUp : undefined}
            >
              {renderSideContent(1)}
              {renderQrCode(1)}
            </div>

            {/* Side 2 - Back */}
            <div
              className="absolute inset-0 overflow-hidden"
              style={{
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
                transform: "rotateY(180deg)",
              }}
              onMouseMove={qrCodeSide === 2 ? handleMouseMove : undefined}
              onMouseUp={qrCodeSide === 2 ? handleMouseUp : undefined}
              onMouseLeave={qrCodeSide === 2 ? handleMouseUp : undefined}
            >
              {renderSideContent(2)}
              {renderQrCode(2)}
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center gap-4 mt-3 shrink-0">
        <div
          className={`text-xs ${
            currentSide === 1
              ? "font-bold text-light-primary dark:text-dark-primary"
              : "text-gray-500 dark:text-gray-400"
          }`}
        >
          Side 1:{" "}
          {orderType === "database" && qrCodeSide === 1 ? "QR Code" : "Text"}
        </div>
        <div className="text-gray-400 dark:text-gray-600">|</div>
        <div
          className={`text-xs ${
            currentSide === 2
              ? "font-bold text-light-primary dark:text-dark-primary"
              : "text-gray-500 dark:text-gray-400"
          }`}
        >
          Side 2:{" "}
          {orderType === "database" && qrCodeSide === 2 ? "QR Code" : "Text"}
        </div>
      </div>
      {selectedDesign && (
        <div className="mt-2 text-center shrink-0">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Style: <strong>{selectedDesign.name}</strong> •{" "}
            {selectedDesign.description}
          </p>
        </div>
      )}
      <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 text-center shrink-0">
        {orderType === "database"
          ? `QR code will appear on Side ${qrCodeSide}. Use the flip button in the form to see both sides.`
          : "Use the flip button in the form to see both sides"}
      </p>
    </div>
  );
};

export default TagPreview;
